import { useCallback, useEffect, useMemo, useState } from 'react';
import { SHARE_LINK_MAX_TTL_HOURS, SHARE_LINK_TTL_PRESETS_HOURS } from '@platform/shared-types';
import { RubyGemMark } from '../../components/RubyGemMark';
import { toastService } from '../../services/toastService';
import {
  documentsApi, publicDocumentUrl, shareDocumentUrl,
} from '../../services/documentsApi';
import type { DocumentShare } from '../../services/documentsApi';
import { formatUtcStamp } from './legalFormat';

/** Clipboard write with a textarea fallback (iOS WebView / non-secure origins). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * SHARE A DOCUMENT — the approved mockup's share modal
 * (platform-app/mockups/system-documents-and-legal-center.html).
 *
 * The permanent link always exists; for a PRIVATE document it opens only when a
 * share token rides on it. A share link is the permanent link + `?share=<token>`
 * and expires after the chosen lifetime (1h … 365d). Email is deferred.
 *
 * Ruby Till V2 chrome: ink header + ruby underline + ruby icon tile, white
 * body, black primary button. Red = brand or destructive only (Revoke).
 */

const INK = '#0A0A0D';

const PRESET_LABEL: Record<number, string> = { 1: '1 hour', 24: '24 hours', 168: '7 days', 720: '30 days' };

function relative(iso: string, now: number): string {
  const ms = Date.parse(iso) - now;
  const abs = Math.abs(ms);
  const h = Math.round(abs / 3_600_000);
  const unit = h < 1 ? `${Math.max(1, Math.round(abs / 60_000))}m` : h < 48 ? `${h}h` : `${Math.round(h / 24)}d`;
  return ms >= 0 ? `expires in ${unit}` : `expired ${unit} ago`;
}

export default function ShareDocumentModal({
  documentId, title, slug, visibility, canEdit, onClose,
}: {
  documentId: string;
  title: string;
  slug: string;
  visibility: 'public' | 'private';
  canEdit: boolean;
  onClose: () => void;
}) {
  const [ttl, setTtl] = useState<number | 'custom'>(24);
  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState<'hours' | 'days'>('days');
  const [note, setNote] = useState('');
  const [shares, setShares] = useState<DocumentShare[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const permanent = publicDocumentUrl(slug);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loadShares = useCallback(async () => {
    setListError(null);
    try {
      const r = await documentsApi.listShares(documentId);
      setShares(r.shares || []);
    } catch (e: any) {
      setListError(e?.message || 'shares_unavailable');
      setShares([]);
    }
  }, [documentId]);

  useEffect(() => { void loadShares(); }, [loadShares]);

  const flash = (key: string) => {
    setCopied(key);
    window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
  };

  const copy = async (text: string, key: string) => {
    if (await copyText(text)) flash(key);
    else toastService.error('Could not copy — select the link and copy it by hand.');
  };

  /** Hours to request, or an error string. */
  const hours: number | string = useMemo(() => {
    if (ttl !== 'custom') return ttl;
    const n = Number(customValue);
    if (!customValue.trim() || !Number.isFinite(n) || n <= 0) return 'Enter a positive number.';
    const h = customUnit === 'days' ? n * 24 : n;
    if (h > SHARE_LINK_MAX_TTL_HOURS) return `The longest a link can last is ${SHARE_LINK_MAX_TTL_HOURS / 24} days.`;
    return h;
  }, [ttl, customValue, customUnit]);

  const create = async () => {
    if (typeof hours !== 'number') return;
    setCreating(true);
    try {
      const r = await documentsApi.createShare(documentId, {
        expiresInHours: hours,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      const url = shareDocumentUrl(r.slug || slug, r.share.token);
      if (await copyText(url)) {
        toastService.success('Share link created and copied');
        flash(r.share.token);
      } else {
        toastService.success('Share link created — use Copy in the list below');
      }
      setNote('');
      setShares((s) => [r.share, ...(s || []).filter((x) => x.token !== r.share.token)]);
    } catch (e: any) {
      toastService.error(e?.message || 'share_create_failed');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token: string) => {
    setRevoking(token);
    try {
      const r = await documentsApi.revokeShare(documentId, token);
      setShares((s) => (s || []).map((x) => (x.token === token ? { ...x, ...r.share, active: false } : x)));
      toastService.success('Share link revoked');
    } catch (e: any) {
      toastService.error(e?.message || 'share_revoke_failed');
    } finally {
      setRevoking(null);
    }
  };

  const now = Date.now();
  const sorted = useMemo(() => {
    const list = [...(shares || [])];
    list.sort((a, b) => (Number(b.active) - Number(a.active)) || String(b.createdAt).localeCompare(String(a.createdAt)));
    return list;
  }, [shares]);

  const isPrivate = visibility === 'private';
  const pill = (on: boolean) => `min-h-[44px] rounded-full border px-4 text-[13px] font-semibold transition-colors ${
    on ? 'border-[#DC1A3C] bg-[#DC1A3C] text-white' : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
  }`;
  const btn = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 text-[13px] font-semibold text-stone-700 hover:border-stone-400 disabled:opacity-50';
  const input = 'min-h-[44px] w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-[13px] text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[rgba(10,10,13,.55)] p-4 sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-doc-title"
        className="w-full max-w-[540px] overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center gap-3 px-5 py-4 text-white" style={{ background: INK }}>
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#FFF1F3]">
            <RubyGemMark size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="share-doc-title" className="truncate text-[16px] font-semibold">Share · {title}</h3>
            <p className="mt-0.5 text-[12px] text-stone-400">
              {isPrivate
                ? 'Private document — only people with a share link can open it'
                : 'Public document — anyone with the link can open it'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-stone-400 hover:bg-white/10 hover:text-white"
          >
            ✕
          </button>
        </div>
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg,#DC1A3C,#9F1239 60%,transparent)' }} />

        <div className="space-y-5 px-5 py-5">
          <div>
            <label className="mb-1.5 block text-[12px] font-bold text-stone-700">Permanent link</label>
            <div className="flex gap-2">
              <input readOnly value={permanent} className={`${input} font-mono`} onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className={btn} onClick={() => void copy(permanent, 'permanent')}>
                {copied === 'permanent' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-1.5 text-[11.5px] text-stone-500">
              {isPrivate
                ? 'This document is private: the permanent link only opens with a share link below.'
                : 'Anyone can open this link. It always shows the version in force.'}
            </p>
          </div>

          {canEdit && (
            <>
              <div>
                <label className="mb-1.5 block text-[12px] font-bold text-stone-700">Private link — expires after</label>
                <div className="flex flex-wrap gap-1.5">
                  {SHARE_LINK_TTL_PRESETS_HOURS.map((h) => (
                    <button key={h} type="button" className={pill(ttl === h)} onClick={() => setTtl(h)}>
                      {PRESET_LABEL[h] ?? `${h}h`}
                    </button>
                  ))}
                  <button type="button" className={pill(ttl === 'custom')} onClick={() => setTtl('custom')}>
                    Custom…
                  </button>
                </div>
                {ttl === 'custom' && (
                  <div className="mt-2 flex gap-2">
                    <input
                      inputMode="decimal"
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder={customUnit === 'days' ? 'e.g. 90' : 'e.g. 48'}
                      className={`${input} max-w-[140px]`}
                      aria-label="Custom lifetime"
                    />
                    <div className="inline-flex overflow-hidden rounded-full border border-stone-200">
                      {(['hours', 'days'] as const).map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setCustomUnit(u)}
                          className={`min-h-[44px] px-4 text-[12.5px] font-semibold ${customUnit === u ? 'text-white' : 'text-stone-500'}`}
                          style={customUnit === u ? { background: INK } : undefined}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {ttl === 'custom' && typeof hours === 'string' && customValue && (
                  <p className="mt-1.5 text-[11.5px] text-[#C4173A]">{hours}</p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] font-bold text-stone-700" htmlFor="share-note">
                  Who is it for? (optional, for your records)
                </label>
                <input
                  id="share-note"
                  value={note}
                  maxLength={200}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Acme Corp legal review"
                  className={input}
                />
              </div>
            </>
          )}

          <div>
            <label className="mb-1.5 block text-[12px] font-bold text-stone-700">Share links</label>
            <div className="divide-y divide-stone-100 rounded-lg border border-stone-200 text-[13px]">
              {shares === null && (
                <div className="flex items-center justify-center py-4"><RubyGemMark size={22} drawing /></div>
              )}
              {shares !== null && sorted.length === 0 && (
                <div className="px-3 py-3 text-stone-500">
                  {listError ? `Could not load share links (${listError}).` : 'No share links yet.'}
                </div>
              )}
              {sorted.map((s) => {
                const label = s.note || s.createdByEmail || 'Share link';
                const status = s.revokedAt
                  ? `revoked ${formatUtcStamp(s.revokedAt)}`
                  : relative(s.expiresAt, now);
                return (
                  <div
                    key={s.token}
                    className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${s.active ? '' : 'text-stone-400'}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate ${s.active ? 'font-medium text-stone-900' : ''}`}>{label}</span>
                      <span className="block text-[11.5px] text-stone-500">{status}</span>
                    </span>
                    {s.active ? (
                      <span className="flex gap-1.5">
                        <button
                          type="button"
                          className={btn}
                          onClick={() => void copy(shareDocumentUrl(slug, s.token), s.token)}
                        >
                          {copied === s.token ? 'Copied' : 'Copy'}
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            disabled={revoking === s.token}
                            onClick={() => void revoke(s.token)}
                            className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-[#C4173A] bg-white px-4 text-[13px] font-semibold text-[#C4173A] hover:bg-[#FFF1F3] disabled:opacity-50"
                          >
                            {revoking === s.token ? 'Revoking…' : 'Revoke'}
                          </button>
                        )}
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-stone-200 px-5 py-3.5 sm:flex-row sm:justify-end">
          <button type="button" className={btn} onClick={onClose}>Close</button>
          {canEdit && (
            <button
              type="button"
              disabled={creating || typeof hours !== 'number'}
              onClick={() => void create()}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-5 text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ background: INK }}
            >
              {creating && <RubyGemMark size={16} drawing color="currentColor" />}
              Create link &amp; copy
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
